'use client'

import { Icon } from '@components/primitives/Icon'
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons/faSpinnerThird'
import ExternalRoutes from '@lib/routes/ExternalRoutes'
import type { AlgoliaTrialHit } from '@modules/trials/search/const'
import type { AlgoliaSearchResultAndUiState } from '@modules/trials/search/getDataFromAlgolia'
import getUniqueTrialsFromTrialLocations from '@modules/trials/search/helpers/getUniqueTrialsFromTrialLocations'
import { fitMapToGeojson } from '@modules/trials/search/map/fitMapToGeojson'
import { handleMapClick } from '@modules/trials/search/map/handleMapClick'
import transformAlgoliaHitsToGeoJSON from '@modules/trials/search/map/transformAlgoliaHitsToGeoJSON'
import type { Hit } from 'instantsearch.js'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css' // required so that markers stay in place
import dynamic from 'next/dynamic'
import { use, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { type ViewStateChangeEvent } from 'react-map-gl/dist/es5/types'
import {
  MAP_SOURCE_ID,
  clusterCountLayer,
  clusterLayer,
  unclusteredLabelLayer,
  unclusteredPointLayer,
} from './MapboxLayers'
import TrialCardPreviewOverlay from './TrialCardPreviewOverlay.client'
import TrialPreviewSidebar from './TrialPreviewSidebar.client'

// Dynamically import the Map component so it only loads on the client
const Map = dynamic(
  async () => import('react-map-gl/dist/es5/components/map'),
  { ssr: false },
)

const Layer = dynamic(
  async () => import('react-map-gl/dist/es5/components/layer'),
  { ssr: false },
)

const Source = dynamic(
  async () => import('react-map-gl/dist/es5/components/source'),
  { ssr: false },
)

const MAPTILER_BASIC_LIGHT_MAP_ID = 'c88f1a8d-eb91-41d6-b9ca-5f51e0fbeaac'

export default function MapResults({
  dataFromAlgoliaPromise,
}: {
  dataFromAlgoliaPromise: Promise<AlgoliaSearchResultAndUiState>
}) {
  const { algoliaResponse, searchLanderPageData } = use(dataFromAlgoliaPromise)
  const { geolocation } = searchLanderPageData.locationData ?? {}

  const trialLocations = algoliaResponse.hits
  const uniqueTrials = getUniqueTrialsFromTrialLocations(trialLocations)

  // Extract the Lat Long from the initialGeolocation

  const [lat, lng] = geolocation
    ? [geolocation.latitude, geolocation.longitude]
    : [39.8283, -98.5795] // fallback to geographic center of contiguous US (near Lebanon, Kansas)

  const [isLoading, setIsLoading] = useState(true)
  const [isOverlayOpen, setIsOverlayOpen] = useState(false)
  const [mapPositionState, setMapPositionState] = useState({
    latitude: lat,
    longitude: lng,
    zoom: geolocation ? 10 : 4, // Use zoom level 4 when geolocation is null for a zoomed-out view
  })

  // FIXME: Hiding to unblock Paid test we will add this back as a button to refine by map bounds
  // Add two hooks to accommodate the map bounds and the aroundLatLng
  // const { refine } = useGeoSearch()
  // const { refine: refineConfigure } = useConfigure({
  //   aroundLatLng: undefined,
  // })

  const { inView, ref } = useInView({ threshold: 0, triggerOnce: true })

  // Populate the sidebar with the unique trials
  const [trialsToPreview, setTrialsToPreview] =
    useState<Hit<AlgoliaTrialHit>[]>(uniqueTrials)
  const [previewTrial, setPreviewTrial] = useState<
    Hit<AlgoliaTrialHit> | undefined
  >(trialsToPreview.at(0))

  // Add state to track the currently clicked feature ID
  const [clickedFeatureId, setClickedFeatureId] = useState<
    string | number | null
  >(null)

  const mapRef = useRef<maplibregl.Map | null>(null)

  // Convert to GeoJSON to use clustering
  const geojsonData = useMemo(
    () => transformAlgoliaHitsToGeoJSON(trialLocations),
    [trialLocations, algoliaResponse],
  )

  // FIXME: Hiding to unblock Paid test we will add this back as a button to refine by map bounds
  // const lastRefinedBoundsRef = useRef<maplibregl.LngLatBounds | null>(null)
  // const onMoveEnd = () => {
  //   maybeRefineByMapBounds({
  //     lastRefinedBoundsRef,
  //     map: mapRef.current,
  //     refine,
  //     refineConfigure,
  //   })
  // }

  useEffect(() => {
    if (trialsToPreview.length > 0) {
      setPreviewTrial(trialsToPreview[0])
    }
  }, [trialsToPreview])

  useEffect(() => {
    setTrialsToPreview(uniqueTrials || [])
  }, [algoliaResponse])

  return (
    <div className='relative grid h-[calc(100vh-152px)] w-screen lg:h-[calc(100vh-198px)]'>
      <div className='z-mapOverlay my-6 ml-6 hidden w-[320px] overflow-y-auto lg:block'>
        <TrialPreviewSidebar
          setIsOverlayOpen={setIsOverlayOpen}
          setPreviewTrial={setPreviewTrial}
          trialGroup={trialsToPreview}
        />
      </div>
      <TrialCardPreviewOverlay
        isOverlayOpen={isOverlayOpen}
        previewTrial={previewTrial}
        setIsOverlayOpen={setIsOverlayOpen}
        setPreviewTrial={setPreviewTrial}
        trialsToPreview={trialsToPreview}
      />
      <div className='absolute inset-0 animate-blurOut' ref={ref}>
        {isLoading && (
          <div className='absolute inset-0 z-20 flex items-center justify-center bg-neutral100'>
            <Icon className='animate-spin' icon={faSpinnerThird} size='3x' />
          </div>
        )}
        {inView && (
          <Map
            {...mapPositionState}
            attributionControl={false}
            mapLib={maplibregl}
            mapStyle={ExternalRoutes.mapTilerMapIdUrl(
              MAPTILER_BASIC_LIGHT_MAP_ID,
            )}
            minZoom={4}
            onClick={(event: mapboxgl.MapLayerMouseEvent) => {
              handleMapClick({
                clickedFeatureId,
                event,
                mapRef,
                setClickedFeatureId,
                setIsOverlayOpen,
                setTrialsToPreview,
                trialLocations,
              })
            }}
            onLoad={(event: any) => {
              mapRef.current = event.target
              setIsLoading(false)
              fitMapToGeojson({
                geojsonData,
                map: mapRef.current,
              })
            }}
            onMove={(event: ViewStateChangeEvent) => {
              setMapPositionState(event.viewState)
            }}
            style={{ height: '100%', width: '100%' }}
          >
            <Source
              cluster={true}
              clusterRadius={50}
              data={geojsonData}
              id={MAP_SOURCE_ID}
              type='geojson'
            >
              {/* Trial Counters and Labels */}
              <Layer {...clusterLayer} />
              <Layer {...clusterCountLayer} />
              <Layer {...unclusteredPointLayer} />
              <Layer {...unclusteredLabelLayer} />
            </Source>
          </Map>
        )}
      </div>
    </div>
  )
}
